/**
 *
 * @module bamIdentityManager
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Services from './../../services/entry';
import Token from './../../token/entry';

const Util = Services.Util;
const TokenManager = Token.TokenManager;
const IdentityTokenStorage = Token.IdentityTokenStorage;
const OneTimePasscodeRequestReason = Services.Identity.Bam.OneTimePasscodeRequestReason;
const BamIdentityManagerConfiguration = Services.Configuration.BamIdentityManagerConfiguration;
const BamIdentityClient = Services.Identity.Bam.BamIdentityClient;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    string,
    object,
    instanceStrict,
    nonEmptyString,
    nonEmptyObject,
    includes
} = Check.assert;

/**
 *
 * @class BamIdentityManager
 *
 */
export default class BamIdentityManager {

    /**
     *
     * @param {SDK.Services.Configuration.BamIdentityManagerConfiguration} config
     * @param {SDK.Services.Identity.Bam.BamIdentityClient} bamIdentityClient
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Token.IdentityTokenStorage} identityTokenStorage
     * @param {SDK.Token.TokenManager} tokenManager
     *
     */
    constructor(config, bamIdentityClient, logger = Logging, tokenManager, identityTokenStorage) {

        const errorMessage = {
            ctor: `${this.constructor.name}(config, bamIdentityClient, logger, tokenManager, identityTokenStorage)`,
            get config() {
                return `${this.ctor} needs SDK.Services.Configuration.BamIdentityManagerConfiguration instance`;
            },
            get bamIdentityClient() {
                return `${this.ctor} needs SDK.Services.Identity.BamIdentityClient instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            },
            get identityTokenStorage() {
                return `${this.ctor} needs SDK.Token.IdentityTokenStorage instance`;
            }
        };

        instanceStrict(config, BamIdentityManagerConfiguration, errorMessage.config, InvalidArgumentException);
        instanceStrict(bamIdentityClient, BamIdentityClient, errorMessage.bamIdentityClient, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);
        instanceStrict(identityTokenStorage, IdentityTokenStorage, errorMessage.identityTokenStorage, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.BamIdentityManagerConfiguration}
         *
         */
        this.config = config;

        /**
         *
         * @access private
         * @type {SDK.Services.BamIdentityClient}
         *
         */
        this.client = bamIdentityClient;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Services.Token.IdentityTokenStorage}
         *
         */
        this.identityTokenStorage = identityTokenStorage;

        this.logger.log(BamIdentityManager.name, 'Created');

    }

    /**
     *
     * @access private
     * @returns {Promise<Boolean>}
     *
     */
    requiresAuthentication() {

        const { identityToken } = this;

        const hasIdentityToken = Check.assigned(identityToken);
        const isIdentityTokenValid = Util.getSafe(() => identityToken.expiration) > Date.now();

        if (hasIdentityToken && isIdentityTokenValid) {
            return Promise.resolve(false);
        }

        return Promise.resolve(true);

    }

    /**
     *
     * @access private
     * @param {String} email - The email associated with the identity.
     * @param {String} password - The password associated with the identity.
     * @desc Logs into the BAM services with an existing identity.
     * @returns {Promise<SDK.Services.Token.IdentityToken>} An awaitable action that
     * completes when the authenticate operation has succeeded.
     *
     */
    authenticate(email, password) {

        const errorMessage = {
            method: `${this.constructor.name}.authenticate(email, password)`,
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get password() {
                return `${this.method} password should be a String with length`;
            }
        };

        string(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        string(password, errorMessage.password, InvalidArgumentException);
        nonEmptyString(password, errorMessage.password, InvalidArgumentException);

        const { accessToken, client, identityTokenStorage } = this;

        return client.authenticate(email, password, accessToken).then((identityToken) => {

            return identityTokenStorage.saveIdentityToken(identityToken);

        }).then(() => {

            return Promise.resolve(this.identityToken);

        });

    }

    /**
     *
     * @access private
     * @param {Object} options
     * @param {String} options.email
     * @param {String} options.password
     * @param {Object} options.attributes
     * @note {Object} [options.metadata] - Contains additional information about the identity being created (ex: isTest)
     * @returns {Promise<Void>}
     *
     */
    createIdentity(options) {
        const errorMessage = {
            method: `${this.constructor.name}.createIdentity(options)`,
            get options() {
                return `${this.method} options should be a non-empty Object`;
            },
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get password() {
                return `${this.method} password should be a String with length`;
            },
            get attributes() {
                return `${this.method} attributes should be an Object`;
            },
            get metadata() {
                return `${this.method} metadata should be an Object`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { email, password, attributes, metadata } = options;

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(password, errorMessage.password, InvalidArgumentException);
        object(attributes, errorMessage.attributes, InvalidArgumentException);

        if (Check.assigned(metadata)) {
            object(metadata, errorMessage.metadata, InvalidArgumentException);
        }

        const { client, accessToken } = this;

        return client.createIdentity(email, password, attributes, accessToken, metadata);

    }

    /**
     *
     * @access private
     * @param {String} email
     * @param {String} password
     * @returns {Promise<Void>}
     *
     */
    changeEmail(email, password) {

        const errorMessage = {
            method: `${this.constructor.name}.changeEmail(email, password)`,
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get password() {
                return `${this.method} password should be a String with length`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(password, errorMessage.password, InvalidArgumentException);

        const { client, identityToken } = this;

        return client.changeEmail(email, password, identityToken);

    }

    /**
     *
     * @access private
     * @param {String} newPassword
     * @param {String} [oldPassword]
     * @returns {Promise<Void>}
     *
     */
    changePassword(newPassword, oldPassword) {

        const errorMessage = {
            method: `${this.constructor.name}.changePassword(newPassword, oldPassword)`,
            get newPassword() {
                return `${this.method} newPassword should be a String with length`;
            },
            get oldPassword() {
                return `${this.method} oldPassword should be a String with length`;
            }
        };

        nonEmptyString(newPassword, errorMessage.newPassword, InvalidArgumentException);

        if (Check.assigned(oldPassword)) {
            nonEmptyString(oldPassword, errorMessage.oldPassword, InvalidArgumentException);
        }

        const { client, identityToken } = this;

        return client.changePassword(newPassword, oldPassword, identityToken);

    }

    /**
     *
     * @access private
     * @param {String} email
     * @returns {Promise<Void>}
     *
     */
    requestPasswordReset(email) {

        const errorMessage = {
            method: `${this.constructor.name}.requestPasswordReset(email)`,
            get email() {
                return `${this.method} email should be a String with length`;
            }
        };

        string(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(email, errorMessage.email, InvalidArgumentException);

        const { client, accessToken } = this;

        return client.requestPasswordReset(email, accessToken);

    }

    /**
     *
     * @access private
     * @param {String} newPassword
     * @param {String} resetToken
     * @returns {Promise<Void>}
     *
     */
    resetPassword(newPassword, resetToken) {

        const errorMessage = {
            method: `${this.constructor.name}.resetPassword(newPassword, resetToken)`,
            get newPassword() {
                return `${this.method} newPassword should be a String with length`;
            },
            get resetToken() {
                return `${this.method} resetToken should be a String with length`;
            }
        };

        string(newPassword, errorMessage.newPassword, InvalidArgumentException);
        nonEmptyString(newPassword, errorMessage.newPassword, InvalidArgumentException);
        string(resetToken, errorMessage.resetToken, InvalidArgumentException);
        nonEmptyString(resetToken, errorMessage.resetToken, InvalidArgumentException);

        const { client, accessToken } = this;

        return client.resetPassword(newPassword, resetToken, accessToken);

    }

    /**
     *
     * @access private
     * @returns {Promise<Identity>}
     *
     */
    getIdentity() {

        const { client, identityToken } = this;

        return client.getIdentity(identityToken);

    }

    /**
     *
     * @access private
     * @param {Object} attributes - The attributes to change.
     * @desc Updates the supplied attributes. Omitted attributes are not updated.
     * @returns {Promise<Void>}
     *
     */
    updateAttributes(attributes) {

        const errorMessage = {
            method: `${this.constructor.name}.updateAttributes(attributes)`,
            get attributes() {
                return `${this.method} attributes should be a non empty Object`;
            }
        };

        nonEmptyObject(attributes, errorMessage.attributes, InvalidArgumentException);

        const { client, identityToken } = this;

        return client.updateAttributes(attributes, identityToken);

    }

    /**
     *
     * @access private
     * @since 3.9.0
     * @param {String} email
     * @desc checks if a user's email is registered with BamIdp
     * @returns {Promise<Array<SDK.Services.Identity.Bam.AuthenticationFlow>>} resolves with a flag indicating if the
     * account has been registered with the BamIdp service
     *
     */
    async getAuthenticationFlow(email) {

        const errorMessage = {
            method: `${this.constructor.name}.getAuthenticationFlow(email)`,
            get email() {
                return `${this.method} email should be a String with length`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);

        const { client, accessToken } = this;

        const authenticationFlowResponse = await client.check(email, accessToken);

        return authenticationFlowResponse.operations;

    }

    /**
     *
     * @access private
     * @since 3.9.0
     * @param {String} email - The email to send the passcode to.
     * @param {SDK.Services.Identity.Bam.OneTimePasscodeRequestReason} reason - The intended use of the passcode.
     * @desc Request that a one-time passcode be sent to an email address.
     * @returns {Promise<Void>}
     *
     */
    async requestOneTimePasscode(email, reason) {

        const errorMessage = {
            method: `${this.constructor.name}.requestOneTimePasscode(email, reason)`,
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get reason() {
                return `${this.method} reason should be a valid SDK.Services.Identity.Bam.OneTimePasscodeRequestReason`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        includes(OneTimePasscodeRequestReason, reason, errorMessage.reason, InvalidArgumentException);

        const { client, accessToken } = this;

        return await client.requestOneTimePasscode(email, reason, accessToken);

    }

    /**
     *
     * @access private
     * @since 3.9.0
     * @param {String} email - The email that received the passcode.
     * @param {String} passcode - The passcode sent to the email associated with the identity.
     * @desc Logs the user in with the BAM identity service.
     * @returns {Promise<SDK.Services.Token.IdentityToken>}
     *
     */
    async redeemOneTimePasscode(email, passcode) {

        const errorMessage = {
            method: `${this.constructor.name}.redeemOneTimePasscode(email, passcode)`,
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get passcode() {
                return `${this.method} passcode should be a String with length`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(passcode, errorMessage.passcode, InvalidArgumentException);

        const { client, accessToken, identityTokenStorage } = this;

        return await client.redeemOneTimePasscode(email, passcode, accessToken).then((redeemedPasscodeToken) => {

            return identityTokenStorage.saveIdentityToken(redeemedPasscodeToken);

        }).then(() => {

            return Promise.resolve(this.identityToken);

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the TokenManager instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.tokenManager.getAccessToken();

    }

    /**
     *
     * @access private
     * @desc Grabs the stored IdentityToken from the IdentityTokenStorage instance
     * @returns {SDK.Token.IdentityToken}
     *
     */
    get identityToken() {

        return this.identityTokenStorage.getIdentityToken();

    }

}



// WEBPACK FOOTER //
// ./src/identity/bam/bamIdentityManager.js